import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    IconButton,
    InputAdornment,
    LinearProgress,
    Chip,
    Stack
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Check,
    Close
} from '@mui/icons-material';
import { passwordService } from '../../services/passwordService';

interface PasswordChangeDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    userEmail?: string;
    isFirstLogin?: boolean;
}

interface PasswordValidation {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
}

interface PasswordStrength {
    score: number;
    label: string;
    color: 'error' | 'warning' | 'info' | 'success';
}

const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({
    open,
    onClose,
    onSuccess,
    userEmail,
    isFirstLogin = false
}) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Password validation state
    const [validation, setValidation] = useState<PasswordValidation>({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
    });

    // Password strength calculation
    const calculatePasswordStrength = (password: string): PasswordStrength => {
        const validCount = Object.values(validation).filter(Boolean).length;
        const score = (validCount / 5) * 100;

        if (score < 40) return { score, label: 'Weak', color: 'error' };
        if (score < 70) return { score, label: 'Fair', color: 'warning' };
        if (score < 90) return { score, label: 'Good', color: 'info' };
        return { score, label: 'Strong', color: 'success' };
    };

    // Validate password against rules
    const validatePassword = (password: string): PasswordValidation => {
        return {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
    };

    const handleNewPasswordChange = (value: string) => {
        setNewPassword(value);
        setValidation(validatePassword(value));
        setError(null);
    };

    const handleSubmit = async () => {
        setError(null);

        // Validate all required fields
        if (!currentPassword.trim()) {
            setError('Trenutna lozinka je obavezna');
            return;
        }

        if (!newPassword.trim()) {
            setError('Nova lozinka je obavezna');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Nova lozinka i potvrda se ne poklapaju');
            return;
        }

        // Check if password meets all requirements
        const allValid = Object.values(validation).every(Boolean);
        if (!allValid) {
            setError('Lozinka ne ispunjava sve sigurnosne zahteve');
            return;
        }

        setLoading(true);

        try {
            await passwordService.changePassword({
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword
            });

            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setValidation({
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false
            });

            onSuccess();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Dogodila se greška');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!isFirstLogin) {
            onClose();
        }
    };

    const strength = calculatePasswordStrength(newPassword);
    const isValid = Object.values(validation).every(Boolean) &&
                   newPassword === confirmPassword &&
                   currentPassword.trim() !== '';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={isFirstLogin}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {isFirstLogin ? 'Postavite Vašu Novu Lozinku' : 'Promena Lozinke'}
                    </Typography>
                    {!isFirstLogin && (
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent>
                {isFirstLogin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Dobro došli! Iz sigurnosnih razloga, morate promeniti Vašu privremenu lozinku pre nastavka.
                        {userEmail && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Nalog: {userEmail}
                            </Typography>
                        )}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    {/* Current Password */}
                    <TextField
                        fullWidth
                        label="Trenutna Lozinka"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                    >
                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* New Password */}
                    <TextField
                        fullWidth
                        label="Nova Lozinka"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => handleNewPasswordChange(e.target.value)}
                        margin="normal"
                        required
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Password Strength Indicator */}
                    {newPassword && (
                        <Box sx={{ mt: 1, mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2">Jačina Lozinke:</Typography>
                                <Chip
                                    label={(() => {
                                        if (strength.label === 'Weak') return 'Slaba';
                                        if (strength.label === 'Fair') return 'Osnovna';
                                        if (strength.label === 'Good') return 'Dobra';
                                        return 'Jaka';
                                    })()}
                                    color={strength.color}
                                    size="small"
                                />
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={strength.score}
                                color={strength.color}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>
                    )}

                    {/* Password Requirements */}
                    {newPassword && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                Zahtevi za Lozinku:
                            </Typography>
                            <Stack spacing={0.5}>
                                {[
                                    { key: 'length', label: 'Najmanje 8 karaktera' },
                                    { key: 'uppercase', label: 'Jedno veliko slovo (A-Z)' },
                                    { key: 'lowercase', label: 'Jedno malo slovo (a-z)' },
                                    { key: 'number', label: 'Jedan broj (0-9)' },
                                    { key: 'special', label: 'Jedan specijalni karakter (!@#$%^&*)' }
                                ].map(({ key, label }) => (
                                    <Box key={key} display="flex" alignItems="center" gap={1}>
                                        {validation[key as keyof PasswordValidation] ? (
                                            <Check color="success" fontSize="small" />
                                        ) : (
                                            <Close color="error" fontSize="small" />
                                        )}
                                        <Typography
                                            variant="body2"
                                            color={validation[key as keyof PasswordValidation] ? 'success.main' : 'text.secondary'}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Confirm Password */}
                    <TextField
                        fullWidth
                        label="Potvrdite Novu Lozinku"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={loading}
                        error={confirmPassword !== '' && newPassword !== confirmPassword}
                        helperText={
                            confirmPassword !== '' && newPassword !== confirmPassword
                                ? 'Lozinke se ne poklapaju'
                                : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                {!isFirstLogin && (
                    <Button
                        onClick={onClose}
                        disabled={loading}
                    >
                        Otkaži
                    </Button>
                )}
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || loading}
                    sx={{ minWidth: 120 }}
                >
                    {loading ? 'Menjam...' : 'Promeni Lozinku'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PasswordChangeDialog;
